import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

export default function LoginPage() {
  const { t, loadPageTranslations } = useTranslation();
  const { validateWorldAnvilKey, isAuthenticated, loading } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Charger les traductions pour la page LOGIN
  useEffect(() => {
    loadPageTranslations('LOGIN');
  }, [loadPageTranslations]);
  
  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated && !loading) {
      const from = location.state?.from?.pathname || '/config';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location]);
  
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setErrorMessage('');
    
    const result = await validateWorldAnvilKey(data.apiKey, data.username);
    
    if (!result.success) {
      const baseError = result.error || t('LOGIN', 'INVALID_API_KEY', 'Clé API invalide');
      const fullMessage = result.details ? `${baseError}\n${result.details}` : baseError;
      setErrorMessage(fullMessage);
    } else {
      // La clé est maintenant automatiquement stockée par AuthContext
      debug.log(config, 'Connexion réussie, clé stockée automatiquement');
      
      // Redirection explicite après une authentification réussie
      const from = location.state?.from?.pathname || '/config';
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo et titre */}
        <div className="flex items-center justify-center space-x-2 mb-6">
          <img src="/favicon.svg" alt="WA Bellow" className="w-12 h-12" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">WA Bellow</h1>
            <p className="text-sm text-gray-600">{t('LOGIN', 'WRITING_ASSISTANT', 'Writing Assistant for World Anvil')}</p>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('LOGIN', 'WELCOME', 'Welcome')}
          </h2>
          <p className="text-gray-600">
            {t('LOGIN', 'LOGIN_DESCRIPTION', 'Please enter your World Anvil API key to continue')}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="page-container">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} autoComplete="on">
            {/* Champ username */}
            <div className="wa-form-group">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                {t('LOGIN', 'USERNAME_LABEL', 'Username')}
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  placeholder={t('LOGIN', 'USERNAME_PLACEHOLDER', 'Enter your World Anvil username')}
                  className={`wa-input ${errors.username ? 'error' : ''}`}
                  {...register('username', { 
                    required: t('LOGIN', 'USERNAME_REQUIRED', 'Username is required')
                  })}
                />
              </div>
              {errors.username && (
                <p className="wa-error-message">{errors.username.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('LOGIN', 'USERNAME_HELP', 'Must match your World Anvil account username')}
              </p>
            </div>
            
            {/* Champ clé API */}
            <div className="wa-form-group">
              <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">
                {t('LOGIN', 'API_KEY_LABEL', 'World Anvil API Key')}
              </label>
              <div className="mt-1">
                <input
                  id="apiKey"
                  name="apiKey"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('LOGIN', 'API_KEY_PLACEHOLDER', 'Enter your World Anvil API key')}
                  className={`wa-input ${errors.apiKey || errorMessage ? 'error' : ''}`}
                  {...register('apiKey', { 
                    required: t('LOGIN', 'API_KEY_REQUIRED', 'API key is required')
                  })}
                />
              </div>
              {errors.apiKey && (
                <p className="wa-error-message">{errors.apiKey.message}</p>
              )}
              {errorMessage && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.76 0L3.054 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="wa-error-message whitespace-pre-wrap break-all">
                        {errorMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bouton de connexion */}
            <div>
              <Button
                type="submit"
                variant="primary"
                full
              >
                {t('LOGIN', 'LOGIN', 'Login')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>
          {t('LOGIN', 'SECURE_CONNECTION', 'Your API key is stored securely in your browser')}
        </p>
      </div>
    </div>
  );
}
